import type {
  CarePlanCycleDetail,
  SaveCarePlanCyclePayload,
} from "@/types/care-plan";

import { type CarePlanStep, StepType } from "./types";

/**
 * Derive the full cycle save payload from current `cycles` + `steps` state.
 *
 * Each cycle's from/to step range is reconstructed by scanning the ordered
 * step list (top-level or within a branch, depending on `cycle.branchId`)
 * for the first and last step whose `cycleId` matches.  Cycles with no
 * associated steps are emitted without from/to — the server rejects those
 * via schema validation, so callers must ensure every cycle owns at least
 * one step before saving.
 */
export function buildCyclesPayload(
  cycles: CarePlanCycleDetail[],
  steps: CarePlanStep[],
): SaveCarePlanCyclePayload[] {
  const stepsByBranch = new Map<string | null, CarePlanStep[]>();
  const collect = (list: CarePlanStep[], branchId: string | null): void => {
    const bucket = stepsByBranch.get(branchId) ?? [];
    bucket.push(...list);
    stepsByBranch.set(branchId, bucket);
    for (const step of list) {
      if (step.type === StepType.DECISION && step.branches) {
        for (const branch of step.branches) {
          collect(branch.steps, branch.id);
        }
      }
    }
  };
  collect(steps, null);

  return cycles.map((cycle) => {
    const scope = stepsByBranch.get(cycle.branchId) ?? [];
    // Skip materialized clones (sourceStepId set) — from/to must reference
    // pass-1 originals, which are the only step IDs the server validates
    // cycle ranges against.
    const members = scope
      .filter((s) => s.cycleId === cycle.id && !s.sourceStepId)
      .sort((a, b) => a.stepNumber - b.stepNumber);
    const fromStepId = members[0]?.id;
    const toStepId = members[members.length - 1]?.id;
    return {
      id: cycle.id,
      branchId: cycle.branchId,
      templateCycleId: cycle.templateCycleId,
      parentCycleId: cycle.parentCycleId,
      name: cycle.name,
      passCount: cycle.passCount,
      gapMinutes: cycle.gapMinutes,
      position: cycle.position,
      fromStepId,
      toStepId,
    };
  });
}
